import React from 'react';
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, Delete } from '@mui/icons-material';
import type { CapitalPartner, PlanCapitalAllocation } from '../../../../../models/StockPickTypes';
import { emptyAllocation } from '../../../../../models/StockPickTypes';
import { formatCurrency } from '../../../../../utils/CurrencyFormat';

interface Props {
  partners: CapitalPartner[];
  rows: PlanCapitalAllocation[];
  totalPurchaseCost: number;
  onChange: (rows: PlanCapitalAllocation[]) => void;
}

function maxOnPlan(partner: CapitalPartner, rows: PlanCapitalAllocation[], partnerId: string): number {
  const onThisPlan = rows
    .filter((r) => r.capitalPartnerId === partnerId)
    .reduce((s, r) => s + (r.allocatedAmount || 0), 0);
  const onOtherPlans = (partner.totalAllocated ?? 0) - onThisPlan;
  return (partner.totalContributed ?? 0) - onOtherPlans;
}

const PurchasePartnerAllocations: React.FC<Props> = ({
  partners,
  rows,
  totalPurchaseCost,
  onChange,
}) => {
  const allocatedSum = rows.reduce((s, r) => s + (r.allocatedAmount || 0), 0);
  const remaining = totalPurchaseCost - allocatedSum;
  const mismatch = totalPurchaseCost > 0 && Math.abs(remaining) > 0.01;

  const addRow = () => onChange([...rows, emptyAllocation()]);

  const removeRow = (idx: number) => {
    const next = [...rows];
    next.splice(idx, 1);
    onChange(next.length ? next : [emptyAllocation()]);
  };

  const updateRow = (idx: number, patch: Partial<PlanCapitalAllocation>) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const splitEqually = () => {
    const indices = rows.map((r, i) => (r.capitalPartnerId ? i : -1)).filter((i) => i >= 0);
    if (indices.length === 0 || totalPurchaseCost <= 0) return;
    const per = Math.round((totalPurchaseCost / indices.length) * 100) / 100;
    let left = totalPurchaseCost;
    const next = [...rows];
    indices.forEach((rowIdx, i) => {
      const amt = i === indices.length - 1 ? Math.round(left * 100) / 100 : per;
      left -= amt;
      next[rowIdx] = { ...next[rowIdx], allocatedAmount: amt };
    });
    onChange(next);
  };

  const fillRemainder = (idx: number) => {
    if (remaining <= 0) return;
    updateRow(idx, { allocatedAmount: (rows[idx].allocatedAmount || 0) + remaining });
  };

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2" color="primary">
          Capital partners (from purchase cost)
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={splitEqually} disabled={totalPurchaseCost <= 0}>
            Split equally
          </Button>
          <Button size="small" startIcon={<AddCircleOutline />} onClick={addRow}>
            Add partner
          </Button>
        </Stack>
      </Stack>
      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        Partner shares must total the purchase cost: <strong>{formatCurrency(totalPurchaseCost)}</strong>{' '}
        (gross value + buy charges). Capture new partner money under Capital Partners first.
      </Alert>
      {rows.map((row, idx) => {
        const partner = partners.find((p) => p.id === row.capitalPartnerId);
        const max = partner && row.capitalPartnerId ? maxOnPlan(partner, rows, row.capitalPartnerId) : 0;
        const over = (row.allocatedAmount || 0) > max + 0.01;
        return (
          <Stack key={row.id ?? idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <TextField
              select
              label="Partner"
              size="small"
              value={row.capitalPartnerId ?? ''}
              onChange={(e) => updateRow(idx, { capitalPartnerId: e.target.value })}
              sx={{ minWidth: 220, flex: 1 }}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {partners.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} (available {formatCurrency(p.availableBalance ?? 0)})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Share (INR)"
              type="number"
              size="small"
              value={row.allocatedAmount || ''}
              onChange={(e) => updateRow(idx, { allocatedAmount: Number(e.target.value) || 0 })}
              sx={{ width: 140 }}
              error={over}
              helperText={over && partner ? `Max ${formatCurrency(max)}` : undefined}
            />
            <TextField
              label="Note"
              size="small"
              value={row.note ?? ''}
              onChange={(e) => updateRow(idx, { note: e.target.value })}
              sx={{ flex: 1 }}
            />
            {mismatch && row.capitalPartnerId && (
              <Button size="small" onClick={() => fillRemainder(idx)} sx={{ mt: { sm: 0.5 } }}>
                + remainder
              </Button>
            )}
            <IconButton color="error" onClick={() => removeRow(idx)} disabled={rows.length <= 1}>
              <Delete />
            </IconButton>
          </Stack>
        );
      })}
      <Typography
        variant="body2"
        color={mismatch ? 'error.main' : 'text.secondary'}
        fontWeight={mismatch ? 600 : 400}
      >
        Allocated {formatCurrency(allocatedSum)} / {formatCurrency(totalPurchaseCost)}
        {mismatch && ` — ${remaining > 0 ? 'assign' : 'reduce'} ${formatCurrency(Math.abs(remaining))}`}
      </Typography>
    </Stack>
  );
};

export default PurchasePartnerAllocations;
